import api from './api';
import { UserSubscription } from '../types/subscription.types';

interface CreateCheckoutSessionRequest {
  priceId: string;
  successUrl: string;
  cancelUrl: string;
  utmParams?: {
    utm_source?: string;
    utm_campaign?: string;
    gclid?: string;
  };
}

interface CreateCheckoutSessionResponse {
  sessionId: string;
  url: string;
}

interface CreatePortalSessionRequest {
  returnUrl: string;
}

interface CreatePortalSessionResponse {
  url: string;
}

interface UpdateSubscriptionRequest {
  priceId: string;
}

interface UpdateSubscriptionResponse {
  message: string;
  subscription: any;
  scheduledChange?: {
    newPlanName: string;
    effectiveDate: string;
  };
}

interface PreviewUpgradeRequest {
  priceId: string;
}

interface PreviewUpgradeResponse {
  currentPlan: string;
  newPlan: string;
  prorationAmount: number;
  totalAmount: number;
  currency: string;
  lineItems: Array<{
    description: string;
    amount: number;
  }>;
}

class SubscriptionService {
  /**
   * Create a Stripe Checkout session for subscription
   */
  async createCheckoutSession(
    request: CreateCheckoutSessionRequest
  ): Promise<CreateCheckoutSessionResponse> {
    return await api.post<CreateCheckoutSessionResponse>(
      '/api/subscriptions/create-checkout-session',
      request
    );
  }

  /**
   * Get current subscription status
   */
  async getSubscriptionStatus(): Promise<UserSubscription> {
    return await api.get<UserSubscription>('/api/subscriptions/status');
  }

  /**
   * Cancel subscription at period end
   */
  async cancelSubscription(): Promise<{ message: string; subscription: any }> {
    return await api.post<{ message: string; subscription: any }>(
      '/api/subscriptions/cancel'
    );
  }

  /**
   * Reactivate a canceled subscription
   */
  async reactivateSubscription(): Promise<{ message: string; subscription: any }> {
    return await api.post<{ message: string; subscription: any }>(
      '/api/subscriptions/reactivate'
    );
  }

  /**
   * Preview the prorated amount for an upgrade
   */
  async previewUpgrade(
    request: PreviewUpgradeRequest
  ): Promise<PreviewUpgradeResponse> {
    return await api.post<PreviewUpgradeResponse>(
      '/api/subscriptions/preview-upgrade',
      request
    );
  }

  /**
   * Update subscription to a new plan
   * - Upgrades: Immediate change with prorated billing
   * - Downgrades: Scheduled for end of billing period
   */
  async updateSubscription(
    request: UpdateSubscriptionRequest
  ): Promise<UpdateSubscriptionResponse> {
    return await api.post<UpdateSubscriptionResponse>(
      '/api/subscriptions/update',
      request
    );
  }

  /**
   * Create a Stripe Customer Portal session
   */
  async createPortalSession(
    request: CreatePortalSessionRequest
  ): Promise<CreatePortalSessionResponse> {
    return await api.post<CreatePortalSessionResponse>(
      '/api/subscriptions/create-portal-session',
      request
    );
  }
}

const subscriptionService = new SubscriptionService();
export default subscriptionService;
